import React from 'react';
import { X, AlertTriangle } from 'lucide-react';

/**
 * A reusable confirmation modal component
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Function} props.onClose - Function to call when closing the modal
 * @param {Function} props.onConfirm - Function to call when confirming the action
 * @param {string} props.title - Modal title
 * @param {string} props.message - Modal message content
 * @param {string} props.confirmText - Text for the confirm button
 * @param {string} props.cancelText - Text for the cancel button
 * @param {boolean} props.isDestructive - Whether the action is destructive (changes styling)
 */
export const ConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message, 
  confirmText, 
  cancelText, 
  isDestructive 
}) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-center mb-4">
          {isDestructive ? (
            <div className="bg-red-100 p-2 rounded-full mr-3">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
          ) : (
            <div className="bg-blue-100 p-2 rounded-full mr-3">
              <AlertTriangle className="h-6 w-6 text-blue-600" />
            </div>
          )}
          <h3 className="text-lg text-gray-900 font-medium font-unna">{title}</h3>
          <button 
            onClick={onClose} 
            className="ml-auto bg-gray-200 font-rasa rounded-full p-1 hover:bg-gray-300 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        
        <p className="text-gray-600 font-rasa mb-6">{message}</p>
        
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 font-rasa rounded shadow-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            {cancelText || 'Cancel'}
          </button>
          <button
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className={`px-4 py-2 rounded font-rasa shadow-sm text-white ${isDestructive ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'} transition-colors`}
          >
            {confirmText || 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;